import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import { Card, CardContent } from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Spinner } from "@/shared/components/ui/spinner";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import { rentalService } from "@/features/rentals/services/rental.service";
import {
  useRentalPickupList,
  type PickupListFilters,
} from "@/features/rentals/hooks/useRentalPickupList";
import { PickupFilters } from "@/features/rentals/components/rental-pickup/PickupFilters";
import { PickupRentalsTable } from "@/features/rentals/components/rental-pickup/PickupRentalsTable";
import { RentalPickupDialog } from "@/features/rentals/components/rental-pickup/RentalPickupDialog";
import { RentalCancelDialog } from "@/features/rentals/components/RentalCancelDialog";
import type {
  RentalPayment,
  RentalResponseDTO,
} from "@/features/rentals/types/rental.types";

const DIALOG_MODE = {
  PICKUP: "pickup",
  CANCEL: "cancel",
} as const;

type DialogMode = (typeof DIALOG_MODE)[keyof typeof DIALOG_MODE];

function getTodayRange(): PickupListFilters {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  const ymd = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  return {
    expectedOutStartDate: ymd,
    expectedOutEndDate: ymd,
  };
}

/**
 * Tela "Retirada" — fluxo USER de pickup.
 *
 * Lista por padrão aluguéis `COMPLETE` com `expectedOutAt = hoje`.
 * Linha clicada abre o `RentalPickupDialog` para confirmar a retirada
 * (com checklist de peças para SUIT e novos pagamentos opcionais) ou
 * solicitar o cancelamento. As duas ações usam endpoints dedicados
 * (`/pickup` e `/cancel`) e removem o aluguel da lista local após
 * sucesso (status sai de `COMPLETE`).
 */
export function RetiradaPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialFilters = useMemo(() => getTodayRange(), []);
  const list = useRentalPickupList({ initialFilters });
  const autoOpenedRef = useRef(false);

  const [dialogMode, setDialogMode] = useState<DialogMode | null>(null);
  const [dialogContent, setDialogContent] = useState<DialogMode | null>(null);
  const [selected, setSelected] = useState<RentalResponseDTO | null>(null);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [selectedStockIds, setSelectedStockIds] = useState<number[]>([]);
  const [newPayments, setNewPayments] = useState<RentalPayment[]>([]);
  const [invoiceObservation, setInvoiceObservation] = useState("");

  const openPickup = useCallback((rental: RentalResponseDTO) => {
    setSelected(rental);
    setSelectedStockIds([]);
    setNewPayments([]);
    setInvoiceObservation("");
    setDialogMode(DIALOG_MODE.PICKUP);
    setDialogContent(DIALOG_MODE.PICKUP);
  }, []);

  // Auto-abre a modal quando a página é acessada via deep-link
  // (`/retirada?rentalId=X`) — usado pelos atalhos da listagem de
  // aluguéis. `autoOpenedRef` garante uma única execução por mount; a
  // URL é limpa só depois da resposta para não cancelar o próprio
  // fetch (mudar `searchParams` durante o effect dispara cleanup).
  useEffect(() => {
    if (autoOpenedRef.current) return;
    const idParam = searchParams.get("rentalId");
    if (!idParam) return;
    const id = Number(idParam);
    if (!Number.isInteger(id) || id <= 0) return;
    autoOpenedRef.current = true;

    rentalService
      .getById(id)
      .then((rental) => {
        if (rental.status !== "COMPLETE") {
          toast.error("Este aluguel não está pronto para retirada.");
          return;
        }
        openPickup(rental);
      })
      .catch((error: unknown) => {
        toast.error(getApiErrorMessage(error, "Erro ao carregar aluguel"));
      })
      .finally(() => {
        setSearchParams({}, { replace: true });
      });
  }, [searchParams, setSearchParams, openPickup]);

  const closeDialog = useCallback(() => {
    setDialogMode(null);
  }, []);

  const requestCancel = useCallback(() => {
    setDialogMode(DIALOG_MODE.CANCEL);
    setDialogContent(DIALOG_MODE.CANCEL);
  }, []);

  const closeCancel = useCallback(() => {
    if (selected) {
      setDialogMode(DIALOG_MODE.PICKUP);
      setDialogContent(DIALOG_MODE.PICKUP);
    } else {
      setDialogMode(null);
    }
  }, [selected]);

  const handleApply = useCallback(
    (filters: PickupListFilters) => list.setFilters(filters),
    [list],
  );

  const handleClear = useCallback(() => {
    list.setFilters(getTodayRange());
  }, [list]);

  const handleConfirmPickup = useCallback(async () => {
    if (!selected) return;
    setDialogLoading(true);
    try {
      const trimmedObservation = invoiceObservation.trim();
      await rentalService.pickup(selected.id, {
        payments: newPayments.length > 0 ? newPayments : undefined,
        ...(trimmedObservation
          ? { invoiceObservation: trimmedObservation }
          : {}),
      });
      list.removeLocal(selected.id);
      toast.success("Aluguel retirado com sucesso");
      setSelected(null);
      setSelectedStockIds([]);
      setNewPayments([]);
      setInvoiceObservation("");
      setDialogMode(null);
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, "Erro ao confirmar retirada"));
    } finally {
      setDialogLoading(false);
    }
  }, [list, newPayments, invoiceObservation, selected]);

  const handleConfirmCancel = useCallback(async () => {
    if (!selected) return;
    setDialogLoading(true);
    try {
      await rentalService.cancel(selected.id);
      list.removeLocal(selected.id);
      toast.success("Aluguel cancelado");
      setSelected(null);
      setSelectedStockIds([]);
      setNewPayments([]);
      setInvoiceObservation("");
      setDialogMode(null);
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, "Erro ao cancelar aluguel"));
    } finally {
      setDialogLoading(false);
    }
  }, [list, selected]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Retirada</h1>
          <p className="text-muted-foreground">
            Aluguéis prontos para serem retirados pelo cliente.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={list.reload}
          disabled={list.loading}
          className="gap-2 self-start md:self-auto"
        >
          <RefreshCw className="w-4 h-4" />
          Atualizar
        </Button>
      </div>

      <Card>
        <CardContent className="space-y-6">
          <PickupFilters
            filters={list.filters}
            loading={list.loading}
            onApply={handleApply}
            onClear={handleClear}
          />

          {list.loading && list.data.length === 0 ? (
            <div className="flex justify-center items-center py-16">
              <Spinner className="w-6 h-6" />
            </div>
          ) : list.error && list.data.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-16 text-center">
              <p className="text-destructive">{list.error}</p>
              <Button variant="outline" size="sm" onClick={list.reload}>
                Tentar novamente
              </Button>
            </div>
          ) : list.data.length === 0 ? (
            <div className="py-16 text-center text-muted-foreground">
              Nenhum aluguel pronto para retirada nos filtros aplicados.
            </div>
          ) : (
            <PickupRentalsTable
              rentals={list.data}
              loading={list.loading}
              currentPage={list.page}
              totalPages={list.totalPages}
              onSelect={openPickup}
              onPageChange={list.setPage}
            />
          )}
        </CardContent>
      </Card>

      {dialogContent === DIALOG_MODE.PICKUP && (
        <RentalPickupDialog
          open={dialogMode === DIALOG_MODE.PICKUP}
          rental={selected}
          loading={dialogLoading}
          selectedStockIds={selectedStockIds}
          newPayments={newPayments}
          invoiceObservation={invoiceObservation}
          onSelectedStockIdsChange={setSelectedStockIds}
          onNewPaymentsChange={setNewPayments}
          onInvoiceObservationChange={setInvoiceObservation}
          onClose={closeDialog}
          onConfirm={handleConfirmPickup}
          onRequestCancel={requestCancel}
        />
      )}
      {dialogContent === DIALOG_MODE.CANCEL && (
        <RentalCancelDialog
          open={dialogMode === DIALOG_MODE.CANCEL}
          loading={dialogLoading}
          rental={selected}
          onClose={closeCancel}
          onConfirm={handleConfirmCancel}
        />
      )}
    </div>
  );
}
